/**
 * AdminUniversityBulkUpload — Bulk question import (CSV / XLSX).
 *
 * Workflow:
 *   1. Pick course_code
 *   2. Download template (CSV or XLSX)
 *   3. Upload populated file → PREVIEW (no DB writes)
 *   4. Review errors + duplicates in the preview table
 *   5. Click "Commit Import" to persist valid rows
 */
import React, { useEffect, useRef, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import {
  Loader2, Download, Upload, AlertTriangle, CheckCircle2, FileText, Trash2,
} from "lucide-react";

export default function AdminUniversityBulkUpload() {
  const [courseCode, setCourseCode] = useState("L1-101");
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const fileRef = useRef(null);

  const adminEmail = adminAuth.getEmail?.() || "admin@kcis.com";

  const loadJobs = async () => {
    try {
      const r = await universityApi.qbJobs();
      setJobs(r.data?.items || []);
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { loadJobs(); }, []);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("course_code", courseCode);
      fd.append("admin_email", adminEmail);
      const r = await universityApi.qbPreview(fd);
      setJob(r.data?.job);
      const j = r.data?.job;
      toast.success(`Preview ready: ${j.valid_rows} valid / ${j.duplicate_rows} duplicates / ${j.errors.length} errors`);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    if (!job) return;
    setCommitting(true);
    try {
      const r = await universityApi.qbCommit(job.id, adminEmail);
      toast.success(`Imported ${r.data?.imported} questions`);
      setJob(null);
      loadJobs();
    } catch (err) {
      toast.error(formatApiError(err, "Import failed"));
    } finally { setCommitting(false); }
  };

  return (
    <AdminShell door="university" title="Bulk Question Upload">
      <div data-testid="admin-bulk-upload-page" className="space-y-6">

        {/* Top bar: course code + template downloads */}
        <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Default Course Code
              </label>
              <input
                type="text"
                className="form-input"
                value={courseCode}
                onChange={(e) => setCourseCode(e.target.value.trim().toUpperCase())}
                placeholder="L1-101"
                data-testid="bulk-course-code-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Used as fallback when a row&apos;s <code>course_code</code> column is blank.
              </div>
            </div>
            <a
              href={universityApi.qbTemplateUrl(courseCode, "csv")}
              className="rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-800 text-sm h-10 px-4 inline-flex items-center gap-2"
              data-testid="bulk-template-csv-btn"
            >
              <Download className="w-4 h-4" />CSV Template
            </a>
            <a
              href={universityApi.qbTemplateUrl(courseCode, "xlsx")}
              className="rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-800 text-sm h-10 px-4 inline-flex items-center gap-2"
              data-testid="bulk-template-xlsx-btn"
            >
              <Download className="w-4 h-4" />XLSX Template
            </a>
          </div>

          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={onPickFile}
                className="hidden"
                data-testid="bulk-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium px-5 h-10 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="bulk-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload CSV / XLSX (Preview)
              </button>
              <div className="text-xs text-slate-500">
                Nothing is saved until you confirm the preview below.
              </div>
            </div>
          </div>
        </section>

        {/* Preview */}
        {job && (
          <section className="rounded-2xl border border-purple-200 bg-white p-4 md:p-5" data-testid="bulk-preview-card">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-purple-900">Import Preview</div>
                <div className="font-display text-lg text-slate-900">
                  {job.filename || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setJob(null)}
                  className="rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm h-9 px-4 inline-flex items-center gap-1"
                  data-testid="bulk-discard-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />Discard
                </button>
                <button
                  onClick={commit}
                  disabled={committing || job.valid_rows === 0}
                  className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium h-9 px-4 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="bulk-commit-btn"
                >
                  {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Commit Import ({job.valid_rows})
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <Stat label="Total rows"  value={job.total_rows} />
              <Stat label="Valid"       value={job.valid_rows} tone="green" />
              <Stat label="Duplicates"  value={job.duplicate_rows} tone="amber" />
              <Stat label="Errors"      value={job.errors.length} tone="rose" />
            </div>

            {job.errors.length > 0 && (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="bulk-errors-block">
                <div className="text-xs font-semibold text-rose-800 inline-flex items-center gap-1 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {job.errors.length} row error{job.errors.length === 1 ? "" : "s"} — these rows will NOT be imported
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto text-xs">
                  {job.errors.slice(0, 50).map((e, i) => (
                    <div key={i} className="text-rose-900">
                      Row <b>{e.row_number}</b> · <span className="font-mono">{e.column}</span> — {e.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {job.duplicates && job.duplicates.length > 0 && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="bulk-dupes-block">
                <div className="text-xs font-semibold text-amber-800 mb-1">
                  {job.duplicates.length} duplicate row{job.duplicates.length === 1 ? "" : "s"} skipped
                </div>
                <div className="text-xs text-amber-900">Rows: {job.duplicates.join(", ")}</div>
              </div>
            )}

            <div className="rounded-xl border border-slate-100 overflow-hidden">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">#</th>
                    <th className="text-left px-3 py-2 font-medium">Type</th>
                    <th className="text-left px-3 py-2 font-medium">Question</th>
                    <th className="text-left px-3 py-2 font-medium">Correct</th>
                    <th className="text-left px-3 py-2 font-medium">Points</th>
                    <th className="text-left px-3 py-2 font-medium">Diff.</th>
                  </tr>
                </thead>
                <tbody>
                  {(job.preview_rows || []).slice(0, 50).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2 font-mono text-purple-900">{r.question_type}</td>
                      <td className="px-3 py-2 max-w-[420px] truncate">{r.question_text}</td>
                      <td className="px-3 py-2 text-slate-700">{(r.correct_answer || "").slice(0, 24)}</td>
                      <td className="px-3 py-2 text-right">{r.points}</td>
                      <td className="px-3 py-2 text-slate-600">{r.difficulty}</td>
                    </tr>
                  ))}
                  {(job.preview_rows || []).length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400">No valid rows yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Recent jobs */}
        <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Recent Imports</div>
          {jobs.length === 0 ? (
            <div className="text-sm text-slate-400">No bulk imports yet.</div>
          ) : (
            <div className="rounded-xl border border-slate-100 overflow-hidden">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">When</th>
                    <th className="text-left px-3 py-2 font-medium">File</th>
                    <th className="text-left px-3 py-2 font-medium">Course</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-right px-3 py-2 font-medium">Valid / Total</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id} className="border-t border-slate-100" data-testid={`bulk-job-${j.id}`}>
                      <td className="px-3 py-2 text-slate-500">{new Date(j.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 inline-flex items-center gap-1"><FileText className="w-3 h-3 text-slate-400" />{j.filename}</td>
                      <td className="px-3 py-2 font-mono text-purple-900">{j.course_code || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] ${j.status === "imported" ? "bg-emerald-100 text-emerald-800" : j.status === "preview" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                          {j.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{j.valid_rows} / {j.total_rows}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose:  "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <div className={`rounded-xl border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
